import React, { useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Alert, Box } from '@mui/material';

import { fetchReservationFXRatesIfNeeded } from '~/actions/cart';

import OfferLocationHeading from '~/components/Common/Booking/OfferLocationHeading';

import { ITEM_TYPE_OFFER } from '~/consts/order';
import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';

import ReservationService from '~/services/ReservationService';

import { checkItemComplete } from '~/utils/cart';
import { isLTLEHotelOffer } from '~/utils/offer';

import NoDataWarning from '../NoDataWarning';
import OrderFooter from '../OrderFooter';
import { getAllItems } from '../selectors';
import withOffers from '../withOffers';

import BookingContent from './BookingContent';
import ContactPhone from './ContactPhone';

const MIN_PHONE_LENGTH = 7;

type Props = {
  cart: App.Cart;
  items: Array<App.CartItem>;
  offer: App.CartOffer;
  user: App.User;
  offerNumber: number;
  nextStepLabel: string;
  proceedStep: () => void;
  backStep: () => void;
  ensureReservationFXRates: () => void;
};

function BookingInformation({
  cart,
  offer,
  items,
  offerNumber,
  proceedStep,
  nextStepLabel,
  backStep,
  user,
  ensureReservationFXRates,
}: Props) {
  const { id_user: userId } = useParams<{ id_user: string }>();
  const { currencyCode } = cart;

  const [properties, setProperties] = useState<Array<App.Property>>([]);

  const isStateValid = useMemo(() => items.length > 0, [items]);

  useEffect(() => {
    ensureReservationFXRates();
  }, [ensureReservationFXRates]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const propertyIds = [
          ...new Set(items.filter((item) => item.pkg?.fk_property_id).map((item) => item.pkg.fk_property_id)),
        ];

        const properties = await Promise.all(
          propertyIds.map((propertyId) => ReservationService.getProperty(propertyId)),
        );

        setProperties(properties.map((property) => property.result));
      } catch (error) {
        console.error(`Error fetching properties: ${error}`);
      }
    };

    fetchProperties();
  }, [items]);

  const isBookable = useMemo(() => {
    const hasDerbysoftProperties = properties.some(
      (property) => property.channel_manager === PROPERTY_CHANNEL_MANAGERS.derbysoft,
    );

    const isFlashOffer = isLTLEHotelOffer(offer);

    return !(isFlashOffer && hasDerbysoftProperties);
  }, [properties, offer]);

  const canProceed = useMemo(() => {
    const { contactPhone, contactPhonePrefix } = cart;

    if (items.length === 0 || !isBookable) {
      return false;
    }

    if (!contactPhone || contactPhone.length < MIN_PHONE_LENGTH || !contactPhonePrefix) {
      return false;
    }

    return items.every((item) => {
      return checkItemComplete(item, offer.booking_type, offer.type);
    });
  }, [cart, items, offer.booking_type, offer.type, isBookable]);

  if (!isStateValid) {
    return <NoDataWarning userId={userId} orderType={ITEM_TYPE_OFFER} />;
  }

  return (
    <Box border="solid 1px #d6d6d6" borderRadius={1} px={2} mb={4} data-testid={offer.id_salesforce_external}>
      <Box mb={6}>
        <OfferLocationHeading offerNumber={offerNumber} offer={offer}></OfferLocationHeading>

        {!isBookable && (
          <Alert
            severity="error"
            variant="filled"
            sx={{
              mt: 2,
              mb: 2,
              py: 2,
            }}
          >
            This is a Derbysoft flash offer which is not bookable through admin portal. Please spoof the user and
            continue in customer portal/my escapes.
          </Alert>
        )}

        <ContactPhone user={user} />

        <BookingContent items={items} offer={offer} offerNumber={offerNumber} currencyCode={currencyCode} />
      </Box>

      <OrderFooter
        cart={cart}
        proceedStep={proceedStep}
        canProceed={canProceed}
        backStep={backStep}
        nextStepLabel={nextStepLabel}
      />
    </Box>
  );
}

function mapStateToProps(state: App.State) {
  return {
    cart: state.cart,
    items: getAllItems(state),
  };
}

const mapDispatchToProps = {
  ensureReservationFXRates: fetchReservationFXRatesIfNeeded,
};

export default connect(mapStateToProps, mapDispatchToProps)(withOffers(BookingInformation));
